import type { ComponentProps } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import styles from "./Markdown.module.css";

export type MarkdownProps = {
  readonly text: string;
};

// Agent-authored text is untrusted, so the XSS contract (chat.md §Markdown and
// the XSS contract) is what picks the renderer, not convenience:
//
// `react-markdown` parses to an AST and emits React elements through
// `hast-util-to-jsx-runtime`. It never builds an HTML string, so there is no
// `dangerouslySetInnerHTML` anywhere in the path, and raw HTML in a message is
// *dropped* rather than sanitised — the strongest version of the guarantee the
// hand-rolled tokeniser used to give, and the reason this is not `marked` or
// `markdown-it` (both hand back HTML strings) or `streamdown` (deliberately
// admits HTML via `rehype-raw`, then sanitises it back down).
//
// `remark-gfm` covers what agents actually emit beyond CommonMark: tables,
// strikethrough, task lists and bare autolinks.
//
// What the previous inline-only renderer could not do — and what made a long
// answer arrive as one unbroken wall of text — was every block construct:
// paragraphs, headings, lists, fenced code. Those are the point of this change.

/** Links are the one construct that leaves the app, so they get the full guard. */
const Anchor = ({ children, href, ...rest }: ComponentProps<"a">) => (
  <a {...rest} href={href} rel="noopener noreferrer nofollow" target="_blank">
    {children}
  </a>
);

// A fenced block arrives as <pre><code>, an inline span as a bare <code>. Only
// the former should scroll, so they are styled apart rather than together.
const Pre = ({ children, ...rest }: ComponentProps<"pre">) => (
  <pre {...rest} className={styles.pre}>
    {children}
  </pre>
);

// A wide table has to scroll inside the bubble; without this the message column
// itself stretches and takes the whole chat with it.
const Table = ({ children, ...rest }: ComponentProps<"table">) => (
  <div className={styles.tableWrap}>
    <table {...rest}>{children}</table>
  </div>
);

const COMPONENTS = {
  a: Anchor,
  pre: Pre,
  table: Table,
} as const;

const PLUGINS = [remarkGfm];

/**
 * Renders a message body as markdown.
 *
 * Block-level constructs render as real elements; raw HTML never does. Styling
 * lives in `Markdown.module.css` so every surface that renders agent text gets
 * the same typography.
 */
export const Markdown = ({ text }: MarkdownProps) => (
  <div className={styles.markdown}>
    <ReactMarkdown components={COMPONENTS} remarkPlugins={PLUGINS}>
      {text}
    </ReactMarkdown>
  </div>
);
